import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { Subscription, catchError, of, tap } from 'rxjs'

import { AgentEvent } from '@yoshu-lib/models/agent-event.model'
import { ChatMessage, NewMessage } from '@yoshu-lib/models/chat-message.model'

import { ChatsService } from '@yoshu-lib/core/api/chats/chats.service'
import {
  ProtocolSession,
  ProtocolStepPayload
} from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'
import { ProtocolSessionsService } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.service'
import { ChatStore } from '@yoshu-lib/core/chat/chat.store'
import { ToastService } from '@yoshu-lib/core/toast/toast.service'

import { ProtocolHelperOpenedEvent, StepModeGatewayService } from './step-mode-gateway.service'
import { buildActionButtons, updateContextForInput, updateStepHistory } from './step-mode.utils'

interface ProtocolHelperItem {
  id: string
  label?: string
  icon?: string
  chat_id?: string | null
}

interface ProtocolHelperRegistryItem extends ProtocolHelperItem {
  stepKey: string
}

type HelperChatStatus = 'idle' | 'loading' | 'ready' | 'error'

@Injectable({ providedIn: 'root' })
export class StepModeFacadeService {
  private readonly sessionsService = inject(ProtocolSessionsService)
  private readonly gateway = inject(StepModeGatewayService)
  private readonly chatsService = inject(ChatsService)
  private readonly chatStore = inject(ChatStore)
  private readonly toast = inject(ToastService)
  private readonly destroyRef = inject(DestroyRef)

  private protocolSubscribedFor: string | null = null
  private protocolSubscriptions = new Subscription()
  private chatSubscribedFor: string | null = null
  private chatSubscriptions = new Subscription()
  private helperSubscriptions = new Subscription()
  private lastStepId: string | null = null
  private readonly helperJoinedChats = new Set<string>()
  readonly sessionId = signal<string | null>(null)
  readonly sessionDetails = signal<ProtocolSession | null>(null)
  readonly currentStep = signal<ProtocolStepPayload | null>(null)
  readonly workflow = signal<string | null>(null)

  readonly isLoading = signal<boolean>(true)
  readonly isClosed = signal<boolean>(false)
  readonly agentOutput = signal<string>('')
  readonly agentError = signal<string | null>(null)
  readonly isAgentStreaming = signal<boolean>(false)
  readonly stepHistory = signal<ProtocolSession['step_history']>([])
  readonly localContext = signal<Record<string, unknown>>({})
  readonly helpers = signal<ProtocolHelperItem[]>([])
  readonly helperChats = signal<Record<string, string>>({})
  readonly helperRegistry = signal<Record<string, ProtocolHelperRegistryItem>>({})
  readonly activeHelperKey = signal<string | null>(null)
  readonly helperChatStatus = signal<Record<string, HelperChatStatus>>({})
  private readonly protocolNameOverride = signal<string | null>(null)

  constructor() {
    this.gateway
      .listenSessionCreated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!event?.session_id) {
          return
        }

        const name = typeof event.protocol_name === 'string' ? event.protocol_name.trim() : ''
        if (name && this.sessionId() === event.session_id) {
          this.setProtocolNameOverride(name)
        }
      })
  }

  readonly availableTransitions = computed(
    () => this.currentStep()?.available_transitions?.map((t) => t.trigger) ?? []
  )

  readonly inputOptions = computed(() => {
    const step = this.currentStep()
    if (!step || !step.config || typeof step.config !== 'object') {
      return []
    }

    const options = step.config['options']
    if (!Array.isArray(options)) {
      return []
    }

    return options.map((option) => ({ value: option, label: `${option}` }))
  })

  readonly stepOrder = computed(() => {
    const revision = this.sessionDetails()?.revision
    if (!revision || typeof revision === 'string') {
      return []
    }

    const steps = revision.steps ?? []

    return steps.map((step) => step.id)
  })

  readonly actionButtons = computed(() =>
    buildActionButtons(this.currentStep(), this.availableTransitions())
  )

  readonly activeHelperChatId = computed(() => {
    const key = this.activeHelperKey()
    if (!key) {
      return null
    }

    return this.helperChats()[key] ?? null
  })

  readonly activeHelperMeta = computed(() => {
    const key = this.activeHelperKey()
    if (!key) {
      return null
    }

    return this.helperRegistry()[key] ?? null
  })

  readonly currentStepKey = computed(() => {
    const step = this.currentStep()
    return step ? this.resolveHelperStepKey(step) : null
  })

  readonly activeHelperIdForStep = computed(() => {
    const stepKey = this.currentStepKey()
    const activeKey = this.activeHelperKey()

    if (!stepKey || !activeKey) {
      return null
    }

    const prefix = `${stepKey}.`
    if (!activeKey.startsWith(prefix)) {
      return null
    }

    return activeKey.slice(prefix.length)
  })

  readonly helperMessages = computed(() => {
    const chatId = this.activeHelperChatId()
    return chatId ? this.chatStore.messagesForChat()(chatId) : []
  })

  readonly hasHelpersOnStep = computed(() => this.helpers().length > 0)
  readonly canSendHelperMessage = computed(() => {
    const chatId = this.activeHelperChatId()
    return Boolean(chatId && this.hasHelpersOnStep() && this.isActiveHelperForCurrentStep())
  })

  readonly isHelperChatLoading = computed(() => {
    const chatId = this.activeHelperChatId()
    if (!chatId) {
      return false
    }

    return this.helperChatStatus()[chatId] === 'loading'
  })

  readonly isHelperChatError = computed(() => {
    const chatId = this.activeHelperChatId()
    if (!chatId) {
      return false
    }

    return this.helperChatStatus()[chatId] === 'error'
  })

  readonly hasHelperChats = computed(() => Object.keys(this.helperChats()).length > 0)

  readonly currentInputValue = computed(() => {
    const step = this.currentStep()
    if (!step || step.step_type !== 'input') {
      return null
    }

    const config =
      step.config && typeof step.config === 'object' ? (step.config as Record<string, unknown>) : {}
    const savesTo = typeof config['saves_to'] === 'string' ? config['saves_to'] : ''
    if (!savesTo) {
      return null
    }

    const path = savesTo.startsWith('context.') ? savesTo.slice('context.'.length) : savesTo
    if (!path) {
      return null
    }

    return this.getValueAtPath(this.localContext(), path)
  })

  initRoutingContext(route: ActivatedRoute) {
    const sessionId = route.snapshot.paramMap.get('sessionId')
    if (sessionId) {
      this.sessionId.set(sessionId)
    }

    const workflow = route.snapshot.parent?.data?.['workflow'] ?? route.snapshot.data['workflow']
    if (workflow) {
      this.workflow.set(workflow)
    }
  }

  loadSession(sessionId: string) {
    this.isLoading.set(true)
    this.sessionId.set(sessionId)

    return this.sessionsService.getSessionById(sessionId).pipe(
      tap((session) => {
        this.sessionDetails.set(session)
        this.stepHistory.set(session.step_history ?? [])
        this.localContext.set(session.context ?? {})
        this.applyProtocolNameOverride()
        this.applyProtocolNameFromCache(sessionId)
        this.lastStepId = session.current_step?.id ?? null
      }),
      tap(() => this.isLoading.set(false)),
      catchError((err) => {
        console.error('Failed to load protocol session', err)
        this.isLoading.set(false)
        this.toast.error('Failed to load protocol session')
        return of(void 0)
      })
    )
  }

  subscribeProtocol(sessionId: string) {
    if (this.protocolSubscribedFor === sessionId) {
      return
    }

    this.resetProtocolSubscriptions()
    this.resetHelperSubscriptions()
    this.protocolSubscribedFor = sessionId
    this.sessionId.set(sessionId)

    this.initHelperSubscriptions()

    this.protocolSubscriptions.add(
      this.gateway.listenStep().subscribe((step) => {
        this.currentStep.set(step)
        this.isLoading.set(false)
        if (step.step_type === 'end') {
          this.markSessionCompleted()
        }
        this.updateStepHistory(step)
        this.updateHelpersForStep(step)
        this.resetAgentState(step)
        this.subscribeChatForStep(step)
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenEnd().subscribe(() => {
        this.markSessionCompleted()
        this.isLoading.set(false)
        this.toast.success('Protocol completed')
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenError().subscribe((err) => {
        this.toast.error(err?.message || 'Protocol error')
        this.isLoading.set(false)
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenHelperOpened().subscribe((event) => {
        this.handleHelperOpened(event)
      })
    )
  }

  sendTransition(trigger: string, value?: unknown) {
    if (trigger === 'next' && typeof value !== 'undefined') {
      this.localContext.update((current) =>
        updateContextForInput({ step: this.currentStep(), value, context: current })
      )
    }

    this.gateway.transition(trigger, value)
    this.isLoading.set(true)
  }

  goBack() {
    this.gateway.goBack()
    this.isLoading.set(true)
  }

  resumeSession(sessionId: string) {
    this.isLoading.set(true)
    this.subscribeProtocol(sessionId)
    this.gateway.resumeSession(sessionId)
  }

  resetState() {
    this.resetProtocolSubscriptions()
    this.resetChatSubscriptions()
    this.resetHelperSubscriptions()
    this.lastStepId = null
    this.sessionId.set(null)
    this.sessionDetails.set(null)
    this.currentStep.set(null)
    this.workflow.set(null)
    this.isLoading.set(true)
    this.isClosed.set(false)
    this.agentOutput.set('')
    this.agentError.set(null)
    this.isAgentStreaming.set(false)
    this.stepHistory.set([])
    this.localContext.set({})
    this.helpers.set([])
    this.helperChats.set({})
    this.helperRegistry.set({})
    this.activeHelperKey.set(null)
    this.helperChatStatus.set({})
    this.protocolNameOverride.set(null)
  }

  setProtocolNameOverride(name: string | null) {
    const trimmed = typeof name === 'string' ? name.trim() : ''
    if (!trimmed) {
      return
    }

    this.protocolNameOverride.set(trimmed)
    this.applyProtocolNameOverride()
  }

  openHelper(helperId: string) {
    if (!helperId) {
      return
    }

    this.gateway.openHelper(helperId)
  }

  selectHelper(helperId: string) {
    const step = this.currentStep()
    const stepKey = step ? this.resolveHelperStepKey(step) : null
    if (!stepKey) {
      return
    }

    const helperKey = this.buildHelperKey(stepKey, helperId)
    this.activeHelperKey.set(helperKey)

    const chatId = this.helperChats()[helperKey]
    if (chatId) {
      this.ensureHelperChatLoaded(chatId)
      return
    }

    this.openHelper(helperId)
  }

  sendHelperMessage(message: string) {
    const chatId = this.activeHelperChatId()
    if (!chatId || !this.canSendHelperMessage()) {
      return
    }

    const content = message.trim()
    if (!content) {
      return
    }

    this.gateway.sendChatMessage({
      chat_id: chatId,
      workflow: 'protocol_helper',
      message: content,
      images: []
    })
  }

  private resetProtocolSubscriptions() {
    this.protocolSubscriptions.unsubscribe()
    this.protocolSubscriptions = new Subscription()
    this.protocolSubscribedFor = null
  }

  private resetChatSubscriptions() {
    this.chatSubscriptions.unsubscribe()
    this.chatSubscriptions = new Subscription()
    this.chatSubscribedFor = null
  }

  private resetHelperSubscriptions() {
    this.helperSubscriptions.unsubscribe()
    this.helperSubscriptions = new Subscription()
    this.helperJoinedChats.clear()
  }

  private markSessionCompleted() {
    this.isClosed.set(true)
    this.sessionDetails.update((current) => {
      if (!current) {
        return current
      }

      if (current.status === 'completed') {
        return current
      }

      return {
        ...current,
        status: 'completed'
      }
    })
  }

  private applyProtocolNameOverride() {
    const protocolName = this.protocolNameOverride()
    if (!protocolName) {
      return
    }

    this.sessionDetails.update((current) => {
      if (!current) {
        return current
      }

      const context = { ...(current.context ?? {}) }
      context['protocol_name'] = protocolName
      return {
        ...current,
        context
      }
    })

    this.localContext.update((current) => ({
      ...current,
      protocol_name: protocolName
    }))
  }

  private applyProtocolNameFromCache(sessionId: string) {
    const cached = this.gateway.getCachedSessionName(sessionId)
    if (!cached) {
      return
    }

    this.setProtocolNameOverride(cached)
  }

  startNewSession() {
    const protocolSlug = this.sessionDetails()?.protocol_slug
    if (!protocolSlug) {
      this.toast.error('Failed to start new session')
      return of(null)
    }

    this.isLoading.set(true)
    this.isClosed.set(false)

    return this.sessionsService.createSession({ protocol_slug: protocolSlug }).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((err) => {
        console.error('Failed to start new session', err)
        this.isLoading.set(false)
        this.toast.error('Failed to start new session')
        return of(null)
      })
    )
  }

  private subscribeChatForStep(step: ProtocolStepPayload) {
    if (step.step_type !== 'ai') {
      this.resetChatSubscriptions()
      return
    }

    const chatId =
      step.config && typeof step.config === 'object' && typeof step.config['chat_id'] === 'string'
        ? (step.config['chat_id'] as string)
        : null

    if (!chatId || this.chatSubscribedFor === chatId) {
      return
    }

    this.resetChatSubscriptions()
    this.chatSubscribedFor = chatId
    this.gateway.joinChatRoom(chatId)

    this.chatSubscriptions.add(
      this.gateway.listenChatMessages().subscribe((event) => {
        if (!event || event.chat_id !== chatId) {
          return
        }

        const message = event.message
        if (!message || message.role !== 'assistant') {
          return
        }

        const text = this.getTextFromMessage(message)
        if (text) {
          this.agentOutput.set(text)
        }

        if (message.status === 'error' && message.error_message) {
          this.agentError.set(message.error_message)
        }
      })
    )

    this.chatSubscriptions.add(
      this.gateway.listenChatAgentEvents().subscribe((event: AgentEvent) => {
        if (!event || event.chat_id !== chatId) {
          return
        }

        this.updateOutputFromMessages(event.messages)

        switch (event.event_name) {
          case 'AgentStartEvent':
            this.isAgentStreaming.set(true)
            this.agentError.set(null)
            this.agentOutput.set('')
            return
          case 'AgentUpdateEvent':
            this.isAgentStreaming.set(true)
            return
          case 'AgentEndEvent':
            this.isAgentStreaming.set(false)
            return
          case 'AgentErrorEvent':
            this.isAgentStreaming.set(false)
            this.agentError.set(event.error_message ?? 'Agent error')
            return
          default:
            return
        }
      })
    )
  }

  private updateStepHistory(step: ProtocolStepPayload) {
    const result = updateStepHistory({
      currentStepId: step.id,
      lastStepId: this.lastStepId,
      history: this.stepHistory(),
      localContext: this.localContext(),
      stepOrder: this.stepOrder()
    })

    this.stepHistory.set(result.history)
    this.lastStepId = result.lastStepId

    if (result.restoredContext) {
      this.localContext.set(result.restoredContext)
    }
  }

  private resetAgentState(step: ProtocolStepPayload) {
    if (step.step_type !== 'ai') {
      this.agentOutput.set('')
      this.agentError.set(null)
      this.isAgentStreaming.set(false)
      return
    }

    this.agentOutput.set('')
    this.agentError.set(null)
    this.isAgentStreaming.set(false)
  }

  private updateHelpersForStep(step: ProtocolStepPayload) {
    const helpers = this.normalizeHelpers(step)
    this.helpers.set(helpers)

    const stepKey = this.resolveHelperStepKey(step)
    if (stepKey) {
      this.helperRegistry.update((current) => {
        const next = { ...current }
        for (const helper of helpers) {
          const helperKey = this.buildHelperKey(stepKey, helper.id)
          next[helperKey] = { ...helper, stepKey }
        }
        return next
      })
    }

    if (!stepKey || helpers.length === 0) {
      this.ensureActiveHelperSelection()
      return
    }

    const helperChats = helpers
      .filter((helper) => typeof helper.chat_id === 'string' && helper.chat_id)
      .map((helper) => ({ key: this.buildHelperKey(stepKey, helper.id), chatId: helper.chat_id! }))

    if (helperChats.length) {
      this.helperChats.update((current) => {
        const next = { ...current }
        for (const item of helperChats) {
          next[item.key] = item.chatId
        }
        return next
      })
    }

    this.ensureActiveHelperSelection(stepKey, helpers)
  }

  private handleHelperOpened(event: ProtocolHelperOpenedEvent | null | undefined) {
    if (!event?.helper_id || !event.chat_id) {
      return
    }

    const step = this.currentStep()
    const stepKey = step ? this.resolveHelperStepKey(step) : null
    if (!stepKey) {
      return
    }

    const helperKey = this.buildHelperKey(stepKey, event.helper_id)
    this.activeHelperKey.set(helperKey)

    this.helperChats.update((current) => ({
      ...current,
      [helperKey]: event.chat_id
    }))

    this.helpers.update((helpers) =>
      helpers.map((helper) =>
        helper.id === event.helper_id ? { ...helper, chat_id: event.chat_id } : helper
      )
    )

    this.ensureHelperChatLoaded(event.chat_id)
  }

  private normalizeHelpers(step: ProtocolStepPayload): ProtocolHelperItem[] {
    const rawHelpers = Array.isArray(step.helpers)
      ? step.helpers
      : (step.metadata?.['helpers'] as unknown)

    if (!Array.isArray(rawHelpers)) {
      return []
    }

    const helpers = rawHelpers.map((helper): ProtocolHelperItem | null => {
      if (!helper || typeof helper !== 'object') {
        return null
      }

      const record = helper as Record<string, unknown>
      const id = typeof record['id'] === 'string' ? record['id'] : ''
      if (!id) {
        return null
      }

      return {
        id,
        label: typeof record['label'] === 'string' ? record['label'] : undefined,
        icon: typeof record['icon'] === 'string' ? record['icon'] : undefined,
        chat_id: typeof record['chat_id'] === 'string' ? record['chat_id'] : null
      }
    })

    return helpers.filter((helper): helper is ProtocolHelperItem => helper !== null)
  }

  private initHelperSubscriptions() {
    this.helperSubscriptions.add(
      this.gateway.listenChatMessages().subscribe((event) => {
        this.handleHelperChatMessage(event)
      })
    )

    this.helperSubscriptions.add(
      this.gateway.listenChatAgentEvents().subscribe((event) => {
        this.handleHelperAgentEvent(event)
      })
    )
  }

  private handleHelperChatMessage(event: NewMessage | null | undefined) {
    if (!event || !this.isHelperChatId(event.chat_id)) {
      return
    }

    this.chatStore.addMessage(event.chat_id, event.message)
  }

  private handleHelperAgentEvent(event: AgentEvent | null | undefined) {
    if (!event || !this.isHelperChatId(event.chat_id)) {
      return
    }

    if (!event.messages || !event.messages.length) {
      return
    }

    for (const message of event.messages) {
      this.chatStore.addMessage(event.chat_id, message)
    }
  }

  private ensureActiveHelperSelection(stepKey?: string | null, helpers?: ProtocolHelperItem[]) {
    const activeKey = this.activeHelperKey()
    if (activeKey) {
      const chatId = this.helperChats()[activeKey]
      if (chatId) {
        this.ensureHelperChatLoaded(chatId)
      }
      return
    }

    if (stepKey && helpers && helpers.length) {
      const helperWithChat = helpers.find((helper) => helper.chat_id)
      if (helperWithChat) {
        const helperKey = this.buildHelperKey(stepKey, helperWithChat.id)
        this.activeHelperKey.set(helperKey)
        const chatId = this.helperChats()[helperKey]
        if (chatId) {
          this.ensureHelperChatLoaded(chatId)
        }
        return
      }
    }

    const helperKeys = Object.keys(this.helperChats())
    if (helperKeys.length) {
      const helperKey = helperKeys[0]
      this.activeHelperKey.set(helperKey)
      const chatId = this.helperChats()[helperKey]
      if (chatId) {
        this.ensureHelperChatLoaded(chatId)
      }
    }
  }

  private ensureHelperChatLoaded(chatId: string) {
    if (!chatId) {
      return
    }

    if (!this.helperJoinedChats.has(chatId)) {
      this.helperJoinedChats.add(chatId)
      this.gateway.joinChatRoom(chatId)
    }

    const status = this.helperChatStatus()[chatId] ?? 'idle'
    if (status === 'loading' || status === 'ready') {
      return
    }

    this.setHelperChatStatus(chatId, 'loading')
    this.chatsService.getMessages(chatId).subscribe({
      next: (messages) => {
        this.chatStore.setMessages(chatId, messages)
        this.setHelperChatStatus(chatId, 'ready')
      },
      error: () => {
        this.setHelperChatStatus(chatId, 'error')
      }
    })
  }

  private setHelperChatStatus(chatId: string, status: HelperChatStatus) {
    this.helperChatStatus.update((current) => ({
      ...current,
      [chatId]: status
    }))
  }

  private isHelperChatId(chatId: string): boolean {
    if (!chatId) {
      return false
    }

    return Object.values(this.helperChats()).includes(chatId)
  }

  private isActiveHelperForCurrentStep(): boolean {
    const step = this.currentStep()
    const stepKey = step ? this.resolveHelperStepKey(step) : null
    const activeKey = this.activeHelperKey()

    if (!stepKey || !activeKey) {
      return false
    }

    return this.helpers().some((helper) => this.buildHelperKey(stepKey, helper.id) === activeKey)
  }

  private resolveHelperStepKey(step: ProtocolStepPayload): string | null {
    const slug = step.slug
    if (typeof slug === 'string' && slug) {
      return slug
    }

    const id = step.id
    if (typeof id === 'string' && id) {
      return id
    }

    return null
  }

  private buildHelperKey(stepKey: string, helperId: string): string {
    return `${stepKey}.${helperId}`
  }

  private updateOutputFromMessages(messages?: ChatMessage[]) {
    if (!messages || !messages.length) {
      return
    }

    const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant')
    if (!lastAssistant || !Array.isArray(lastAssistant.content)) {
      return
    }

    const text = this.getTextFromMessage(lastAssistant)

    if (text) {
      this.agentOutput.set(text)
    }
  }

  private getTextFromMessage(message: ChatMessage): string {
    if (typeof message.content === 'string') {
      return message.content
    }

    if (!Array.isArray(message.content)) {
      return ''
    }

    return message.content
      .filter((part): part is { type: 'text'; text: string } => part?.type === 'text')
      .map((part) => part.text ?? '')
      .join('')
  }

  private getValueAtPath(source: Record<string, unknown>, path: string): unknown {
    if (!path) {
      return undefined
    }

    return path.split('.').reduce<unknown>((acc, key) => {
      if (acc == null) {
        return undefined
      }

      if (typeof acc !== 'object') {
        return undefined
      }

      return (acc as Record<string, unknown>)[key]
    }, source)
  }
}
